/**
 * `/lehrer/session/:id`: everything around one session except the game
 * itself. PIN and QR code for students, links to the board and to the
 * phone control, start and end, the lobby (rename, remove, lock).
 *
 * The game controls live on `/steuerung/:id` so they fit a phone screen.
 */
import type {
  ErrorPayload,
  SessionInfo,
  SessionParticipant,
  SessionStatePayload,
} from '@schulspiele/protocol';

import { createLifetime } from '@shared/lifetime';
import { api } from '@shared/api';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { confirmDialog, openDialog, promptDialog } from '@shared/ui/dialog';
import { pinDigits, renderChips, setCount } from '@shared/ui/chips';
import { qrSvg } from '@shared/ui/qr';
import { toast, toastError } from '@shared/ui/toast';
import { SessionClient } from '@shared/ws-client';
import { connectionBanner, workLayout } from '../layout';
import { currentTeacher, phasePill, renderLogin } from './common';

export function joinUrl(pin: string): string {
  return `${location.origin}/spielen?pin=${encodeURIComponent(pin)}`;
}

/**
 * The game is gone (a server restart forgets every game, U19) or this
 * teacher may not open it: a plain card with the way back, never a page
 * that still looks like a running game.
 */
export function goneCard(error?: ErrorPayload): string {
  // Not "gone" but refused (logged out by a new password, another teacher's game): say why.
  const gone = error === undefined || error.code === 'no_such_session';
  return `<div class="muted-box stack--tight" style="justify-items:center" data-gone>
    <h2>${gone ? t('session.gone') : esc(error.reason ?? t('error.generic'))}</h2>
    ${gone ? `<p class="muted">${t('session.goneHint')}</p>` : ''}
    <a class="btn" href="/lehrer" data-link>${t('session.toTeacher')}</a>
  </div>`;
}

/** Ask, then send `session/end`; the button waits for the next state (two quick taps, U19). */
export function confirmEnd(
  c: SessionClient,
  button: HTMLButtonElement,
  phase: SessionStatePayload['phase'],
  over: boolean,
  onSent?: () => void,
): void {
  const close = over;
  const ranking = phase === 'running' && !over;
  void confirmDialog(
    t(
      close ? 'session.confirmClose' : ranking ? 'session.confirmEndRanking' : 'session.confirmEnd',
    ),
    { confirmLabel: t(close ? 'session.close' : 'session.end'), danger: true },
  ).then((ok) => {
    if (!ok) return;
    onSent?.();
    button.disabled = true;
    c.command('session/end', close ? { close: true } : undefined).catch((err: unknown) => {
      button.disabled = false;
      toastError(err);
    });
  });
}

/** "Alle grauen entfernen" (U19): shown while at least one phone is away. */
export function updateKickOffline(
  button: HTMLButtonElement,
  participants: SessionParticipant[],
  phase: SessionStatePayload['phase'],
): void {
  const away = participants.filter((p) => !p.connected).length;
  button.hidden = away === 0 || phase === 'ended';
  button.dataset.count = String(away);
}

export function kickOffline(c: SessionClient, button: HTMLButtonElement): void {
  const count = Number(button.dataset.count ?? '0');
  void confirmDialog(t('session.confirmKickOffline', { count }), {
    confirmLabel: t('session.kickOffline'),
    danger: true,
  }).then((ok) => {
    if (ok) c.command('session/kickOffline').catch(toastError);
  });
}

export function renderSessionPage(mount: HTMLElement, sessionId: string): () => void {
  let client: SessionClient | null = null;
  const life = createLifetime();

  void (async () => {
    const teacher = await currentTeacher();
    if (life.isDisposed()) return;
    if (teacher === null) {
      renderLogin(mount, () => {
        renderSessionPage(mount, sessionId);
      });
      return;
    }
    let info: SessionInfo;
    try {
      info = await api<SessionInfo>(`/api/sessions/${encodeURIComponent(sessionId)}`);
    } catch {
      workLayout(mount, goneCard(), { teacherName: teacher.displayName });
      return;
    }
    if (life.isDisposed()) return;

    const boardUrl = `/tafel/${encodeURIComponent(info.id)}?token=${encodeURIComponent(info.boardToken ?? '')}`;
    const main = workLayout(
      mount,
      `<div data-banner></div>
       <div class="page-head">
         <div>
           <h1>${esc(info.gameName)}</h1>
           <div data-phase>${phasePill(info.phase)}</div>
         </div>
         <a class="btn btn--ghost btn--small" href="/lehrer" data-link>${t('session.back')}</a>
       </div>
       <div class="session-grid">
         <div class="card pin-card" data-pin-card>
           <p class="pin-card__label">${t('session.pin')}</p>
           <div class="pin-big" data-pin aria-label="${esc(info.pin)}">${pinDigits(info.pin)}</div>
           <div data-qr></div>
           <p class="muted" style="margin:0">${t('session.joinAt')}<br /><span class="join-url">${esc(location.host)}/spielen</span></p>
         </div>
         <div class="stack">
           <div class="card stack--tight">
             <h2 style="margin:0" data-steps-title>${t('session.steps')}</h2>
             <ol class="stack--tight" style="margin:0;padding-left:1.25rem" data-steps></ol>
             <div class="actions">
               <a class="btn" href="${esc(boardUrl)}" target="_blank" rel="noopener" data-board-link>${t('session.openBoard')}</a>
               <a class="btn" href="/steuerung/${esc(info.id)}" data-link>${t('session.openControl')}</a>
               <button class="btn btn--ghost" data-start type="button" disabled>${t('session.start')}</button>
               <button class="btn btn--danger" data-end type="button" disabled>${t('session.end')}</button>
             </div>
           </div>
           <div class="card stack--tight" data-lobby>
             <div class="section__head" style="margin:0">
               <h2 style="margin:0" data-count></h2>
               <div class="actions">
                 <button class="btn btn--small btn--ghost" data-kick-offline type="button" hidden>${t('session.kickOffline')}</button>
                 <button class="btn btn--small btn--ghost" data-lock type="button" disabled></button>
               </div>
             </div>
             <div class="names" data-names></div>
           </div>
         </div>
       </div>`,
      { teacherName: teacher.displayName },
    );

    client = new SessionClient({ kind: 'teacher', sessionId: info.id });
    const c = client;
    let gone = false;
    c.on('status', (s) => {
      if (!gone) qs(main, '[data-banner]').innerHTML = connectionBanner(s);
    });
    // After a server restart the game is gone: no dead PIN, QR or names.
    c.on('refused', (e) => {
      gone = true;
      main.innerHTML = goneCard(e);
    });
    c.on('error', (e) => {
      if (!gone) toast(e.reason ?? t('error.generic'), 'error');
    });
    c.on('state', (s) => {
      applyState(main, s);
    });
    c.connect();

    let locked = false;
    qs(main, '[data-start]').addEventListener('click', () => {
      c.command('session/start').catch(toastError);
    });
    // As in the control bar: "Beenden" shows the ranking first, then "Schließen" closes.
    let phase: SessionStatePayload['phase'] = 'lobby';
    let over = false;
    const endButton = qs<HTMLButtonElement>(main, '[data-end]');
    endButton.addEventListener('click', () => {
      confirmEnd(c, endButton, phase, over);
    });
    const kickOfflineButton = qs<HTMLButtonElement>(main, '[data-kick-offline]');
    kickOfflineButton.addEventListener('click', () => {
      kickOffline(c, kickOfflineButton);
    });
    qs(main, '[data-lock]').addEventListener('click', () => {
      c.command('session/lock', { locked: !locked }).catch(toastError);
    });
    qs(main, '[data-names]').addEventListener('click', (e) => {
      void handleChipClick(e, c);
    });

    function applyState(root: HTMLElement, s: SessionStatePayload): void {
      locked = s.locked;
      qs(root, '[data-phase]').innerHTML =
        phasePill(s.phase, s.gameOver) +
        (s.gameOver ? ` <span class="muted">${t('session.gameOver')}</span>` : '');
      const qr = qs(root, '[data-qr]');
      if (s.needsStudentDevices && qr.childElementCount === 0) {
        qr.innerHTML = qrSvg(joinUrl(s.pin ?? info.pin), { label: t('session.qrLabel') });
      }
      // After the end the PIN leads nowhere.
      const withPin = s.needsStudentDevices && s.phase !== 'ended';
      qs(root, '[data-pin-card]').hidden = !withPin;
      qs(root, '.session-grid').classList.toggle('session-grid--single', !withPin);
      const steps = s.needsStudentDevices
        ? [t('session.step1'), t('session.step2'), t('session.step3')]
        : [t('session.step1'), t('session.step3NoDevices')];
      // Once it runs, the start steps are history.
      const lobby = s.phase === 'lobby';
      qs(root, '[data-steps-title]').textContent = lobby
        ? t('session.steps')
        : s.phase === 'running'
          ? s.gameOver
            ? t('session.gameOverNote')
            : t('session.runningNote')
          : t('session.endedNote');
      qs(root, '[data-steps]').hidden = !lobby;
      qs(root, '[data-start]').hidden = !lobby;
      qs(root, '[data-steps]').innerHTML = steps.map((x) => `<li>${x}</li>`).join('');
      qs(root, '[data-lobby]').hidden = !s.needsStudentDevices;
      const participants = s.participants ?? [];
      setCount(qs(root, '[data-count]'), t('session.participants', { count: participants.length }));
      // After the end nobody needs renaming or removing (U19).
      renderChips(qs(root, '[data-names]'), participants, {
        controls: s.phase !== 'ended' && !s.gameOver,
        ended: s.phase === 'ended',
        emptyText: t('session.nobodyYet'),
      });
      updateKickOffline(kickOfflineButton, participants, s.phase);
      const lock = qs<HTMLButtonElement>(root, '[data-lock]');
      lock.textContent = locked ? t('session.unlock') : t('session.lock');
      lock.disabled = s.phase === 'ended';
      qs<HTMLButtonElement>(root, '[data-start]').disabled = s.phase !== 'lobby';
      phase = s.phase;
      over = s.gameOver && s.phase !== 'ended';
      endButton.disabled = s.phase === 'ended';
      endButton.hidden = s.phase === 'ended';
      endButton.textContent = t(over ? 'session.close' : 'session.end');
    }
  })();

  return () => {
    life.dispose();
    client?.close();
  };
}

/**
 * A tap on a participant chip (session page and control): a small sheet
 * with "Umbenennen" and "Entfernen" (U19, one big target instead of two
 * small buttons).
 */
export async function handleChipClick(e: Event, c: SessionClient): Promise<void> {
  const chip = (e.target as HTMLElement).closest<HTMLElement>('[data-chip-menu]');
  if (chip === null) return;
  const id = chip.dataset.chipMenu ?? '';
  const current = chip.dataset.name ?? '';
  const choice = await openDialog({
    title: current,
    body: `<p class="muted" style="margin:0">${esc(t('session.kickHint'))}</p>`,
    actions: [
      { label: t('session.cancel'), value: 'cancel', kind: 'ghost' },
      { label: t('session.rename'), value: 'rename', kind: 'ghost' },
      { label: t('session.kick'), value: 'kick', kind: 'danger' },
    ],
  }).result;
  if (choice === 'kick') {
    await c.command('session/kick', { participantId: id }).catch(toastError);
  } else if (choice === 'rename') {
    let typed = current;
    // A taken name: say so and ask again with what was typed.
    for (;;) {
      const name = await promptDialog(t('session.renamePrompt'), {
        title: t('session.renameTitle', { name: current }),
        initial: typed,
        maxLength: 16,
      });
      if (name === null || name.trim() === current) return;
      try {
        await c.command('session/rename', { participantId: id, name });
        return;
      } catch (err) {
        toastError(err);
        typed = name;
      }
    }
  }
}
